import { useMutation } from '@tanstack/react-query'
import { CalendarClock, MailCheck, WifiOff } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui'
import { api, errorMessage } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { formatDateTime } from '@/lib/format'
import { useOnline } from '@/lib/hooks'
import { t } from '@/lib/i18n'
import { maintenancePhase, usePlatformStatus } from '@/lib/platform'
import { MultiSlot } from './slots'
import { ShellBanner } from './ShellBanner'

/** SHELL-018: shown while the browser is offline; queries refetch on reconnect. */
export function OfflineBanner() {
  const online = useOnline()
  if (online) return null
  return (
    <ShellBanner tone="danger" icon={WifiOff}>
      <span className="font-semibold">{t('You are offline.')}</span> {t('We will reconnect as soon as your connection is back.')}
    </ShellBanner>
  )
}

/** SHELL-019: scheduled maintenance notice (the full page takes over once it starts). */
export function MaintenanceBanner() {
  const status = usePlatformStatus()
  const [dismissed, setDismissed] = useState(false)
  if (dismissed || maintenancePhase(status) !== 'scheduled' || !status.maintenance) return null
  const { message, startsAt } = status.maintenance
  return (
    <ShellBanner tone="warning" icon={CalendarClock} onDismiss={() => setDismissed(true)}>
      <span className="font-semibold">{t('Scheduled maintenance')}</span>
      {startsAt ? <> {t('starts {time}.', { time: formatDateTime(startsAt) })}</> : null} {message}
    </ShellBanner>
  )
}

/** Unverified users can browse and join but cannot post, comment, chat or pay. */
export function VerifyEmailBanner() {
  const { me, isLoggedIn, isVerified } = useAuth()
  // FVJ-4: after the code, back to the page the member was on (the post they wanted to comment on).
  const here = useLocation()
  const path = here.pathname + here.search
  const resend = useMutation({
    mutationFn: () => api.post<void>('/api/auth/resend-verification'),
    onSuccess: () => toast.success(t('We sent a new code to {email}', { email: me?.user.email ?? '' })),
    onError: (e) => toast.error(errorMessage(e)),
  })
  if (!isLoggedIn || isVerified) return null
  return (
    <ShellBanner
      tone="info"
      icon={MailCheck}
      action={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="hidden sm:inline-flex" loading={resend.isPending} onClick={() => resend.mutate()}>
            {t('Resend code')}
          </Button>
          <Button size="sm" variant="primary" asChild>
            <Link to={path === '/' ? '/verify-email' : `/verify-email?next=${encodeURIComponent(path)}`}>{t('Enter code')}</Link>
          </Button>
        </div>
      }
    >
      {t('Verify your email to post and comment')}
    </ShellBanner>
  )
}

/** Global banners in priority order, plus any AppBanner slot from the slices. */
export function GlobalBanners({ verify = true }: { verify?: boolean }) {
  return (
    <>
      <OfflineBanner />
      <MaintenanceBanner />
      {verify ? <VerifyEmailBanner /> : null}
      <MultiSlot name="AppBanner" props={{}} />
    </>
  )
}
